import { matchRoutes, type RouteObject } from 'react-router-dom';

/**
 * Generic route-preloading utility. Portals instantiate this with their
 * own route config and permission-scope type via createRoutePreloader().
 */
interface PreloadableChild<TScope> {
  index?: boolean;
  scope?: TScope | TScope[];
  preload?: () => Promise<unknown>;
}

interface PreloadableRoute<TScope> {
  scope?: TScope | TScope[];
  preload?: () => Promise<unknown>;
  children?: PreloadableChild<TScope>[];
}

/**
 * Checks whether the current user satisfies the route's permission requirements.
 */
function hasPermission<TScope>(
  scope: TScope | TScope[] | undefined,
  permissions: Set<TScope>
): boolean {
  if (!scope) return true;
  if (Array.isArray(scope)) return scope.some((s) => permissions.has(s));
  return permissions.has(scope);
}

/**
 * Traverses the route tree and collects preload functions
 * for routes the current user has permission to access.
 */
function collectPreloadables<TScope>(
  routes: PreloadableRoute<TScope>[],
  permissions: Set<TScope>
): Promise<unknown>[] {
  const promises: Promise<unknown>[] = [];

  for (const route of routes) {
    if (!hasPermission(route.scope, permissions)) continue;

    if (route.preload) {
      promises.push(route.preload());
    }

    if (route.children) {
      for (const child of route.children) {
        if (!hasPermission(child.scope, permissions)) continue;
        if (child.preload) {
          promises.push(child.preload());
        }
      }
    }
  }

  return promises;
}

/**
 * Creates a `preloadRoutes` function scoped to a specific portal's route
 * config and permission-scope type. Each call gets its own `hasPreloaded`
 * guard, so separate portals (or a hot-reloaded instance) don't share state.
 */
export function createRoutePreloader<
  TScope,
  TRoute extends PreloadableRoute<TScope>,
>(routes: TRoute[]) {
  let hasPreloaded = false;

  return function preloadRoutes(permissions?: Set<TScope>) {
    if (hasPreloaded) return;
    hasPreloaded = true;

    const preload = async () => {
      const promises = collectPreloadables(
        routes,
        permissions ?? new Set<TScope>()
      );
      await Promise.allSettled(promises);
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preload, { timeout: 3000 });
    } else {
      setTimeout(preload, 3000);
    }
  };
}

interface PathRoute {
  index?: boolean;
  path?: string;
  preload?: () => Promise<unknown>;
  children?: PathRoute[];
}

/** The route tree as `matchRoutes` wants it, each route's preload on `handle`. */
function toMatchable(routes: PathRoute[]): RouteObject[] {
  return routes.map((route) =>
    route.index
      ? { index: true, handle: route.preload }
      : {
          path: route.path,
          handle: route.preload,
          children: route.children && toMatchable(route.children),
        }
  );
}

/**
 * Starts downloading the code for the page `pathname` opens (and its tab, when
 * it has one), without waiting for sign-in or permissions to settle.
 *
 * The routes above are only preloaded once the permissions are in, so on a
 * hard reload a lazy page's chunk used to be requested only after the whole
 * sign-in sequence finished. Calling this at startup downloads it alongside
 * that sequence instead. The chunk request is the same `import()` the route
 * uses later, so the browser never fetches it twice. Safe to call for a path
 * that matches nothing.
 */
export function preloadRouteForPath(
  routes: PathRoute[],
  pathname: string
): void {
  try {
    const tree: RouteObject[] = [{ path: '/', children: toMatchable(routes) }];
    const matches = matchRoutes(tree, pathname);
    for (const { route } of matches ?? []) {
      if (typeof route.handle === 'function') {
        // A chunk that fails to load is retried by the route itself later.
        Promise.resolve(route.handle()).catch(() => undefined);
      }
    }
  } catch {
    // Preloading is an optimisation: a route shape we cannot match is skipped.
  }
}
